import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";

import { Col, Row } from "reactstrap";

const Pagination = (props) => {
  const [itemPaginator, setItemPaginator] = useState([1, 2, 3, 4, 5]);
  const count = parseInt(props.count) || 0;
  const limit = props.limit || 100;
  const contact = props.contact;
  const pages = Math.ceil(count / limit);

  let next = props.next;
  let previous = props.previous;
  let filter = props.filter;
  let offset = 0;
  let page = 1;
  let search;

  // Calcular página actual basándose en next, previous o parámetros actuales
  if (next) {
    let parameters = new URL(next);
    let nextOffset = parseInt(parameters.searchParams.get("offset")) || 0;
    search = parameters.searchParams.get("search");
    // Si next tiene offset, la página actual es la anterior a next
    page = Math.floor(nextOffset / limit);
  } else if (previous) {
    let parameters = new URL(previous);
    let prevOffset = parseInt(parameters.searchParams.get("offset")) || 0;
    search = parameters.searchParams.get("search");
    // Si previous tiene offset, la página actual es la siguiente a previous
    page = Math.floor(prevOffset / limit) + 2;
  } else {
    // Si no hay next ni previous, calcular desde el offset actual o asumir página 1
    const currentUrl = window.location.search;
    const urlParams = new URLSearchParams(currentUrl);
    const currentOffset = parseInt(urlParams.get('offset')) || 0;
    page = Math.floor(currentOffset / limit) + 1;
  }

  // Asegurar que la página esté dentro del rango válido
  page = Math.max(1, Math.min(page, pages));

  const paginationHandler = (url) => {
    // Extraer la parte de la URL después del baseUrl
    let path = url.split(props.baseUrl);
    let urlPath = path[1] || '';
    
    // Si la URL no empieza con '?', agregarla
    if (urlPath && !urlPath.startsWith('?')) {
      urlPath = '?' + urlPath;
    }
    
    props.filterFunction(urlPath);
  };

  const pageChange = (targetPage) => {
    let url;
    let allParams = new URLSearchParams();
    
    // Extraer todos los parámetros existentes de next, previous o URL actual
    if (next) {
      const nextUrl = new URL(next);
      allParams = new URLSearchParams(nextUrl.searchParams);
    } else if (previous) {
      const prevUrl = new URL(previous);
      allParams = new URLSearchParams(prevUrl.searchParams);
    } else {
      // Si no hay next ni previous, usar los parámetros de la URL actual
      const currentUrl = window.location.search;
      allParams = new URLSearchParams(currentUrl);
    }
    
    // Calcular el offset correcto para la página objetivo (las páginas empiezan en 1)
    const targetOffset = (targetPage - 1) * limit;
    
    // Actualizar offset y limit, manteniendo todos los demás filtros
    allParams.set('limit', limit.toString());
    allParams.set('offset', targetOffset.toString());
    
    // Construir la URL final
    url = `?${allParams.toString()}`;
    
    props.filterFunction(url);
  };

  // Actualizar itemPaginator basándose en la página actual
  const updatePaginator = () => {
    if (pages <= 5) {
      // Si hay 5 páginas o menos, mostrar todas
      const newPaginator = Array.from({ length: pages }, (_, i) => i + 1);
      if (JSON.stringify(newPaginator) !== JSON.stringify(itemPaginator)) {
        setItemPaginator(newPaginator);
      }
    } else {
      // Si hay más de 5 páginas, mostrar ventana deslizante
      let start = Math.max(1, page - 2);
      let end = Math.min(pages, start + 4);
      
      // Ajustar si estamos cerca del final
      if (end - start < 4) {
        start = Math.max(1, end - 4);
      }
      
      const newPaginator = Array.from({ length: end - start + 1 }, (_, i) => start + i);
      if (JSON.stringify(newPaginator) !== JSON.stringify(itemPaginator)) {
        setItemPaginator(newPaginator);
      }
    }
  };

  // Usar useEffect para actualizar el paginador cuando cambie la página o el total de páginas
   useEffect(() => {
     updatePaginator();
   }, [page, pages]);

  return (
    <Row>
      <Col sm="6">
        <p className="mt-2">
          Página {page} de {pages} ({count} resultados)
        </p>
      </Col>
      <Col sm="6">
        <ul className="pagination pagination-rounded justify-content-end">
          <li className={page > 1 ? "page-item" : "page-item disabled"}>
            <Link
              className="page-link"
              to="#"
              aria-label="First"
              onClick={(e) => {
                e.preventDefault();
                if (page > 1) pageChange(1);
              }}
            >
              <i className="mdi mdi-chevron-double-left"></i>
            </Link>
          </li>
          <li className={previous ? "page-item" : "page-item disabled"}>
            <Link
              className="page-link"
              to="#"
              aria-label="Previous"
              onClick={(e) => {
                e.preventDefault();
                if (previous) paginationHandler(previous);
              }}
            >
              &lt;
            </Link>
          </li>
          {itemPaginator.map(
            (item, idx) =>
              item <= pages && (
                <li
                  key={idx}
                  className={item === page ? "page-item active" : "page-item"}
                >
                  <Link
                    className="page-link"
                    to="#"
                    onClick={(e) => {
                      e.preventDefault();
                      pageChange(item);
                    }}
                  >
                    {item}
                  </Link>
                </li>
              )
          )}
          <li className={next ? "page-item" : "page-item disabled"}>
            <Link
              className="page-link"
              to="#"
              aria-label="Next"
              onClick={(e) => {
                e.preventDefault();
                if (next) paginationHandler(next);
              }}
            >
              &gt;
            </Link>
          </li>
          <li className={page < pages ? "page-item" : "page-item disabled"}>
            <Link
              className="page-link"
              to="#"
              aria-label="Last"
              onClick={(e) => {
                e.preventDefault();
                if (page < pages) pageChange(pages);
              }}
            >
              <i className="mdi mdi-chevron-double-right"></i>
            </Link>
          </li>
        </ul>
      </Col>
    </Row>
  );
};

Pagination.propTypes = {
  count: PropTypes.number,
  next: PropTypes.string,
  previous: PropTypes.string,
  limit: PropTypes.number,
  baseUrl: PropTypes.string,
  filterFunction: PropTypes.func,
};

export default Pagination;
